import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiBriefcase, FiCreditCard, FiHome, FiLayers, FiUserPlus } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import companyService from "../../../services/company.service";
import planService from "../../../services/plan.service";
import bankService from "../../../services/bank.service";
import BrandWordmark from "../../../components/brand/BrandWordmark";
import "../../../styles/dashboard.css";
import "../../../styles/brand.css";

export default function SsdiDashboard() {
  const { auth } = useAuth();

  const [stats, setStats] = useState({
    companies: 0,
    plans: 0,
    pending: 0,
    pendingUsers: 0,
    banks: 0,
    pendingBanks: 0,
  });

  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    if (!auth?.token) return;

    try {
      setLoading(true);

      const [companyRes, planRes, banksRes, pendingBanksRes] = await Promise.allSettled([
        companyService.getCompanyStats(auth.token),
        planService.getPlans(auth.token),
        bankService.ssdiListBanks({ page: 1, limit: 1 }),
        bankService.ssdiListBanks({ page: 1, limit: 1, status: "pending_payment" }),
      ]);

      const companyStats =
        companyRes.status === "fulfilled"
          ? companyRes.value?.data || companyRes.value || {}
          : {};

      const plansData =
        planRes.status === "fulfilled"
          ? planRes.value?.data?.plans || planRes.value?.plans || []
          : [];

      const bankTotal = (result) => {
        if (result.status !== "fulfilled") return 0;
        const payload = result.value?.data?.data || result.value?.data || {};
        return Number(payload.total) || 0;
      };

      setStats({
        companies: companyStats.total || 0,
        plans: Array.isArray(plansData) ? plansData.length : 0,
        pending: companyStats.pending || 0,
        pendingUsers: companyStats.pendingUsers || 0,
        banks: bankTotal(banksRes),
        pendingBanks: bankTotal(pendingBanksRes),
      });
    } catch (error) {
      setStats({
        companies: 0,
        plans: 0,
        pending: 0,
        pendingUsers: 0,
        banks: 0,
        pendingBanks: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const cards = [
    {
      to: "/ssdi/companies",
      icon: <FiBriefcase />,
      title: "All Repo Agencies",
      desc: "Create and manage repo agencies",
    },
    {
      to: "/ssdi/registrations",
      icon: <FiUserPlus />,
      title: "Pending Repo Agency Registrations",
      desc: "Review APK sign-ups, payment & activation",
    },
    {
      to: "/ssdi/plans",
      icon: <FiLayers />,
      title: "Agency Activation Plans",
      desc: "Manage plan pricing and tiers",
    },
    {
      to: "/ssdi/payments",
      icon: <FiCreditCard />,
      title: "Repo Agency Payments",
      desc: "Monitor transactions",
    },
    {
      to: "/ssdi/banks",
      icon: <FiHome />,
      title: "Banks / NBFCs",
      desc: "Manage Banks / NBFCs",
      countKey: "banks",
    },
    {
      to: "/ssdi/banks",
      icon: <FiHome />,
      title: "Pending Banks / NBFCs",
      desc: "Manage Banks / NBFCs",
      countKey: "pendingBanks",
    },
  ];

  const renderCard = (card) => (
    <Link key={card.title} to={card.to} className="dashboard-card-link">
      <div className="card action-card dashboard-action-card">
        <div className="action-icon dashboard-action-icon">{card.icon}</div>
        {card.countKey ? (
          <div className="dashboard-action-count">
            {loading ? "..." : stats[card.countKey]}
          </div>
        ) : null}
        <h4>{card.title}</h4>
        <p className="muted small">{card.desc}</p>
      </div>
    </Link>
  );

  return (
    <div className="content">
      <div className="welcome ssdi-dashboard-hero">
        <div className="welcome-left">
          <h2 className="ssdi-dashboard-welcome">
            Welcome to <BrandWordmark className="brand-wordmark--lg" />
          </h2>
          <p className="muted">Manage the full multi-repo agencies platform.</p>
        </div>

        <div className="stats">
          <div className="stat">
            <div className="stat-value">
              {loading ? "..." : stats.companies}
            </div>
            <div className="stat-label">Repo Agencies</div>
          </div>

          <div className="stat">
            <div className="stat-value">{loading ? "..." : stats.plans}</div>
            <div className="stat-label">Plans</div>
          </div>

          <div className="stat">
            <div className="stat-value">{loading ? "..." : stats.pending}</div>
            <div className="stat-label">Pending Repo Agencies</div>
          </div>

          <div className="stat">
            <div className="stat-value">{loading ? "..." : stats.pendingUsers}</div>
            <div className="stat-label">Pending Users</div>
          </div>
        </div>
      </div>

      <div className="starter-cards ssdi-dashboard-actions">
        {cards.map(renderCard)}
      </div>
    </div>
  );
}
